import type { Pfm, PfmInitResult } from '@aviata/platform-client';
import type { AxiosInstance } from 'axios';

import { getAuthMethod } from './lib';
import type { FlowType } from '../../shared/config';

type Props = {
  pfm: Pfm;
  app: FlowType;
  shouldTryRefresh: boolean;
  isDevAuthEnabled: boolean;
  logonHttp: AxiosInstance | undefined;
};

/**
 * @description
 * Freedom auth pfm init strategy.
 * The strategy first tries to refresh the old token and only then authenticates from scratch:
 *
 * 1. If the refresh request returns 2XX, then a browser has valid user and refresh tokens,
 * so we don't need to authenticate the user again
 *
 * 2. If the refresh request returns 4XX, then the user was not previously authenticated,
 * so we need to authenticate him
 */
const checkAndThenAuthStrategy = ({
  pfm,
  app,
  shouldTryRefresh,
  isDevAuthEnabled,
  logonHttp,
}: Props): Promise<PfmInitResult> => {
  if (shouldTryRefresh) {
    return pfm.auth
      .refreshToken({ skip401Retry: true })
      .catch(err => {
        pfm.emitter.emit('refresh-token-error', err);

        const auth = getAuthMethod(
          app,
          pfm,
          isDevAuthEnabled,
          logonHttp ?? pfm.client,
        );
        return auth();
      })
      .then(res => ({ type: 'user', token: res.data.token }));
  }

  const auth = getAuthMethod(
    app,
    pfm,
    isDevAuthEnabled,
    logonHttp ?? pfm.client,
  );
  return auth().then(res => ({ type: 'user', token: res.data.token }));
};

export const initFreedomAuthStrategy = (
  app: FlowType,
  isDevAuthEnabled = false,
  shouldTryRefresh = true,
  logonHttp: AxiosInstance | undefined = undefined,
) => {
  return (pfm: Pfm) =>
    checkAndThenAuthStrategy({
      pfm,
      app,
      shouldTryRefresh,
      isDevAuthEnabled,
      logonHttp,
    });
};
